import { type ApiProblem, type ApiSchemas, call, newIdempotencyKey } from '@rental/contracts/client'
import { format, formatDateTime, formatEuro } from '@rental/i18n'
import { Button, cn, Field, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Store } from 'lucide-react'
import { type FormEvent, useEffect, useId, useRef, useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import {
  type CashDrawer,
  closeNeedsReason,
  type DrawerKind,
  type DrawerOutcome,
  settleOutcome,
} from '../cash/drawer'
import { OpenDrawerForm } from '../cash/OpenDrawer'
import { CASH_QUERY_KEY, CASH_REFRESH_MS, loadCash, MAX_COUNT_CENTS } from '../cash/queries'
import { DataTable } from '../components/DataTable'
import { EmptyState } from '../components/EmptyState'
import { OutcomeFocus, OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { Section } from '../components/Section'
import { type ChipTone, StatusChip } from '../components/StatusChip'
import { ReasonList, SubPanel } from '../components/SubPanel'
import { Guard, LoadState } from '../components/states'
import { Facts, FIELD, FIELD_ROW, NamedFor, ProblemNotice, StaffInput } from '../components/ui'
import {
  differenceFrom,
  TILLS_QUERY_KEY,
  TillClosures,
  TillFields,
  TillForm,
  type TillsRead,
  useTillDraft,
} from '../fiscal/Tills'
import { newTillDraft, type TillBody, type TillDifference, tillBody } from '../fiscal/till'
import { noImplicitSubmit } from '../forms'
import { useLocale } from '../i18n'
import { centsForInput, parseEuroCents } from '../money'
import { problemOf } from '../problems'

type Session = ApiSchemas['StaffCashSession']
type Till = ReturnType<typeof useTillDraft>

/** A point's latest Zs shown under its drawer. */
const ZS_SHOWN = 10

/**
 * Closed is a drawer at rest, not a fault (DESIGN.md, `StatusChip`); open is
 * taking cash; counted is waiting for its close and takes none meanwhile.
 */
const KIND_TONE: Record<DrawerKind, ChipTone> = {
  closed: 'neutral',
  open: 'success',
  counting: 'warning',
}

function kindOf(drawer: CashDrawer): DrawerKind {
  if (!drawer.session) return 'closed'
  return drawer.session.firstCountCents === null ? 'open' : 'counting'
}

/**
 * S5 (PAY-6, Fady 15 Oct): the Cassa screen. A drawer per pick-up point, each
 * on a pane: opened with a count, closed with a blind one. What a drawer
 * should hold is never shown before the count is typed; once it is, the
 * drawer takes no cash, and a count that differs closes only with a reason
 * the Owner reads. The latest closes show both counts. UI-2e: each step a
 * form of its own, saying what its button waits for; what a press did said
 * in the drawer's pane, where the form that sent it was.
 */
export function CashScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  const allowed = can(access, 'payments.record')
  // Read afresh on arrival, as the drawer field does: a cash deposit taken or
  // given back a moment ago changes what this screen says is kept here.
  const cash = useQuery({
    queryKey: CASH_QUERY_KEY,
    queryFn: loadCash,
    enabled: allowed,
    staleTime: 0,
    refetchInterval: CASH_REFRESH_MS,
  })
  // S6 (CMP-3): each point's till closures (Z), read once for all the points.
  const tills = useQuery({
    queryKey: TILLS_QUERY_KEY,
    queryFn: () => load(call(api.GET('/v1/staff/fiscal/till-closures'))),
    enabled: allowed,
  })
  const zsOf = (drawer: CashDrawer): TillsRead => ({
    closures: tills.data?.items
      .filter((closure) => closure.location.id === drawer.location.id)
      .slice(0, ZS_SHOWN),
    problem: problemOf(tills.error),
    retrying: tills.isFetching,
    retry: tills.refetch,
  })

  return (
    <Guard allowed={allowed} title={m.cash.title}>
      <PageHeader title={m.cash.title} intro={m.cash.intro} />
      <LoadState data={cash.data} problem={problemOf(cash.error)}>
        {(data) => (
          <>
            {data.drawers.length === 0 ? (
              <EmptyState icon={Store} title={m.cash.field.noPoint}>
                {m.cash.noPointFills}
              </EmptyState>
            ) : (
              // One above the other, full width: each holds forms, whose fields pair only where both fit.
              <ul className="flex flex-col gap-6">
                {data.drawers.map((drawer) => (
                  <li key={drawer.location.id}>
                    <DrawerSection drawer={drawer} zs={zsOf(drawer)} />
                  </li>
                ))}
              </ul>
            )}
            <History sessions={data.history} />
          </>
        )}
      </LoadState>
    </Guard>
  )
}

function DrawerSection({ drawer, zs }: { drawer: CashDrawer; zs: TillsRead }) {
  const { locale, messages: m } = useLocale()
  const kind = kindOf(drawer)
  const session = drawer.session
  const point = drawer.location.name
  // S6: the till's Z typed at the close: kept between the blind count and the recount.
  const till = useTillDraft(() => newTillDraft(), { suggestNow: true })
  // What a press here just did: said from its own answer, until the drawer moves on from
  // where it left it (UI-2g, `settleOutcome`).
  const [done, setDone] = useState<DrawerOutcome | null>(null)
  const said = settleOutcome(done, kind)
  if (said !== done) setDone(said)
  const record = (what: DrawerOutcome['what']) => setDone({ what, at: Date.now(), seen: false })

  return (
    <Section
      title={point}
      status={<StatusChip tone={KIND_TONE[kind]}>{m.cash.state[kind]}</StatusChip>}
    >
      {said && said.what !== 'counted' ? (
        <OutcomeNotice key={said.at}>
          <p>
            {format(
              said.what === 'closed'
                ? m.cash.closed
                : said.what === 'opened'
                  ? m.cash.opened
                  : m.cash.openedElsewhere,
              { point },
            )}
          </p>
        </OutcomeNotice>
      ) : null}
      {session ? (
        <Facts
          items={[
            [
              m.cash.openedAt,
              format(m.cash.openedLine, {
                date: formatDateTime(locale, session.openedAt),
                name: session.openedBy,
              }),
            ],
            [
              m.cash.openingCount,
              <span key="opening" className="figures">
                {formatEuro(session.openingCents, locale)}
              </span>,
            ],
            ...(drawer.depositsHeldCents > 0
              ? [
                  [
                    m.cash.depositsHeldLabel,
                    <span key="held" className="figures">
                      {formatEuro(drawer.depositsHeldCents, locale)}
                    </span>,
                  ] as const,
                ]
              : []),
          ]}
        />
      ) : null}
      {session && session.outsideTill.length > 0 ? <OutsideTill session={session} /> : null}
      {kind === 'closed' ? (
        <OpenDrawerForm
          drawer={drawer}
          standalone
          onOpened={(here) => record(here ? 'opened' : 'openedElsewhere')}
        />
      ) : null}
      {kind === 'open' && session ? (
        <BlindCount point={point} session={session} till={till} onSent={record} />
      ) : null}
      {kind === 'counting' && session ? (
        <FinishClose
          point={point}
          session={session}
          till={till}
          fresh={said?.what === 'counted'}
          onClosed={() => record('closed')}
        />
      ) : null}
      <Zs locationId={drawer.location.id} point={point} zs={zs} />
    </Section>
  )
}

/**
 * S9 (Q-62): cash recorded into this drawer on trips while it was still in
 * the pocket of whoever drove, by person, before the count that has to find
 * it in the drawer. Only each person's own: what the drawer should hold
 * stays unsaid until the count.
 */
function OutsideTill({ session }: { session: Session }) {
  const { locale, messages: m } = useLocale()
  const copy = m.cash.outsideTill
  const euro = (cents: number) => formatEuro(cents, locale)
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-lg">{copy.title}</h3>
      <p className="text-sm text-text-muted">{copy.intro}</p>
      <Facts
        items={session.outsideTill.map((line) => {
          const net = line.takenCents - line.givenCents
          return [
            line.person.displayName,
            <span key={line.person.id} className="figures">
              {[
                format(copy.taken, { amount: euro(line.takenCents) }),
                ...(line.givenCents > 0
                  ? [format(copy.given, { amount: euro(line.givenCents) })]
                  : []),
                net > 0
                  ? format(copy.handIn, { amount: euro(net) })
                  : net < 0
                    ? format(copy.giveBack, { amount: euro(-net) })
                    : copy.even,
              ].join(', ')}
            </span>,
          ] as const
        })}
      />
    </div>
  )
}

/** Sends a close; a retry of the same one replays, another gets its own key. */
function useClose(sessionId: string, onTillDifference: (difference: TillDifference) => void) {
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const lastSent = useRef<{ body: string; key: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)

  /**
   * `counted`: the count differs, the first one is kept and the drawer now
   * shows what it should hold. `till`: the count matched, the Z did not: its
   * reason is asked for, and the drawer stays open.
   */
  async function send(body: {
    countCents: number
    reason?: string
    till?: TillBody
  }): Promise<'closed' | 'counted' | 'till' | null> {
    setProblem(null)
    const serialized = JSON.stringify(body)
    if (lastSent.current?.body !== serialized) {
      lastSent.current = { body: serialized, key: newIdempotencyKey() }
    }
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/cash/sessions/{sessionId}/close', {
        params: { path: { sessionId }, header: { 'idempotency-key': lastSent.current.key } },
        body,
      }),
    )
    setBusy(false)
    await queryClient.invalidateQueries({ queryKey: CASH_QUERY_KEY })
    if (result.ok) {
      lastSent.current = null
      // The Z typed with it is in the point's list now.
      await queryClient.invalidateQueries({ queryKey: TILLS_QUERY_KEY })
      return 'closed'
    }
    if (result.problem.kind === 'api' && result.problem.code === 'cash_difference') {
      lastSent.current = null
      return 'counted'
    }
    const tillDifference = differenceFrom(result.problem)
    if (tillDifference) {
      lastSent.current = null
      onTillDifference(tillDifference)
      return 'till'
    }
    if (!takeOver(result.problem)) setProblem(result.problem)
    return null
  }

  return { send, busy, problem }
}

/** What a close sends of the till's Z: nothing when none is typed, or what is missing. */
function tillFor(till: Till): { ok: true; till?: TillBody } | { ok: false } {
  const outcome = tillBody(till.draft, { optional: true, difference: till.difference })
  if (!outcome.ok) {
    till.setMissing(outcome.missing)
    return { ok: false }
  }
  return outcome.till ? { ok: true, till: outcome.till } : { ok: true }
}

/** The till's Z, typed at the close when the till has made its daily closure. */
function TillAtClose({ point, till }: { point: string; till: Till }) {
  const { messages: m } = useLocale()
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1 font-medium">
        {m.fiscal.till.title}
        <NamedFor name={point} />
        {till.difference ? null : (
          <span className="font-regular text-text-muted"> ({m.newBooking.optional})</span>
        )}
      </legend>
      <p className="text-sm text-text-muted">{m.fiscal.till.closeHint}</p>
      <TillFields
        draft={till.draft}
        onChange={till.change}
        difference={till.difference}
        missing={till.missing}
        said={point}
      />
    </fieldset>
  )
}

/** The count typed as a euro amount: what it is in cents, and whether the server takes it. */
function useCount(initial: string | (() => string)) {
  const [count, setCount] = useState(initial)
  const cents = parseEuroCents(count)
  const valid = cents !== null && cents <= MAX_COUNT_CENTS
  return { count, setCount, cents: valid ? cents : null, typedWrong: count.trim() !== '' && !valid }
}

/**
 * The first count, typed before anything says what the drawer should hold.
 * Enter sends nothing: the count is kept for good, so only the button sends it.
 */
function BlindCount({
  point,
  session,
  till,
  onSent,
}: {
  point: string
  session: Session
  till: Till
  onSent: (what: 'counted' | 'closed') => void
}) {
  const { messages: m } = useLocale()
  const titleId = useId()
  const reasonsId = useId()
  const { count, setCount, cents, typedWrong } = useCount('')
  const { send, busy, problem } = useClose(session.id, till.setDifference)
  const reasons = cents === null && !busy ? [m.cash.why.count] : []

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (cents === null || busy) return
    const z = tillFor(till)
    if (!z.ok) return
    const outcome = await send({ countCents: cents, ...(z.till ? { till: z.till } : {}) })
    if (outcome === 'closed') till.clear()
    if (outcome === 'closed' || outcome === 'counted') onSent(outcome)
  }

  return (
    <form onSubmit={submit} onKeyDown={noImplicitSubmit} aria-labelledby={titleId} noValidate>
      <SubPanel
        title={
          <span id={titleId}>
            {m.cash.closeTitle}
            <NamedFor name={point} />
          </span>
        }
      >
        <div className={FIELD_ROW}>
          <Field
            className={cn(FIELD, 'max-w-xs')}
            label={
              <>
                {m.cash.closeCount}
                <NamedFor name={point} />
              </>
            }
            hint={m.cash.blindHint}
            required
            error={typedWrong ? m.payment.amountInvalid : null}
          >
            {(control) => (
              <StaffInput
                {...control}
                inputMode="decimal"
                autoComplete="off"
                value={count}
                onChange={(event) => setCount(event.target.value)}
              />
            )}
          </Field>
        </div>
        <TillAtClose point={point} till={till} />
        <ProblemNotice problem={problem} />
        <div>
          <Button
            type="submit"
            variant="staff"
            busy={busy}
            disabled={reasons.length > 0}
            aria-describedby={reasons.length > 0 ? reasonsId : undefined}
          >
            {m.cash.countSubmit}
            <NamedFor name={point} />
          </Button>
        </div>
        <ReasonList id={reasonsId} reasons={reasons} />
      </SubPanel>
    </form>
  )
}

/**
 * The blind count is in, and differs: what the drawer should hold is shown,
 * with the difference. A recount that matches closes it; otherwise the reason
 * is asked for. The first count stays whatever happens. Just counted here,
 * the figures take the focus: the button that sent the count is gone.
 */
function FinishClose({
  point,
  session,
  till,
  fresh,
  onClosed,
}: {
  point: string
  session: Session
  till: Till
  fresh: boolean
  onClosed: () => void
}) {
  const { locale, messages: m } = useLocale()
  const titleId = useId()
  const reasonsId = useId()
  const first = session.firstCountCents ?? 0
  const expected = session.expectedCents ?? 0
  const { count, setCount, cents, typedWrong } = useCount(() => centsForInput(first, locale))
  const [reason, setReason] = useState('')
  const { send, busy, problem } = useClose(session.id, till.setDifference)
  const needsReason = cents !== null && closeNeedsReason(cents, expected)
  const reasonShort = needsReason && reason.trim().length < 3
  const reasons = busy
    ? []
    : [...(cents === null ? [m.cash.why.count] : []), ...(reasonShort ? [m.cash.why.reason] : [])]

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (cents === null || reasonShort || busy) return
    const z = tillFor(till)
    if (!z.ok) return
    const outcome = await send({
      countCents: cents,
      ...(needsReason ? { reason: reason.trim() } : {}),
      ...(z.till ? { till: z.till } : {}),
    })
    if (outcome === 'closed') {
      till.clear()
      onClosed()
    }
  }

  const counted = (
    <Notice tone="warning">
      <p>
        {format(m.cash.counted, {
          first: formatEuro(first, locale),
          expected: formatEuro(expected, locale),
          difference: formatEuro(first - expected, locale),
        })}
      </p>
    </Notice>
  )
  return (
    <form onSubmit={submit} onKeyDown={noImplicitSubmit} aria-labelledby={titleId} noValidate>
      <SubPanel
        title={
          <span id={titleId}>
            {m.cash.closeTitle}
            <NamedFor name={point} />
          </span>
        }
      >
        {fresh ? <OutcomeFocus>{counted}</OutcomeFocus> : counted}
        <p className="text-sm text-text-muted">{m.cash.frozen}</p>
        <div className={FIELD_ROW}>
          <Field
            className={cn(FIELD, 'max-w-xs')}
            label={
              <>
                {m.cash.finalCount}
                <NamedFor name={point} />
              </>
            }
            hint={
              cents !== null
                ? format(m.cash.differenceNow, { difference: formatEuro(cents - expected, locale) })
                : undefined
            }
            required
            error={typedWrong ? m.payment.amountInvalid : null}
          >
            {(control) => (
              <StaffInput
                {...control}
                inputMode="decimal"
                autoComplete="off"
                value={count}
                onChange={(event) => setCount(event.target.value)}
              />
            )}
          </Field>
          {needsReason ? (
            <Field
              className={FIELD}
              label={
                <>
                  {m.cash.reason}
                  <NamedFor name={point} />
                </>
              }
              hint={m.cash.reasonHint}
              required
              error={
                reason.trim().length > 0 && reason.trim().length < 3 ? m.refund.reasonShort : null
              }
            >
              {(control) => (
                <StaffInput
                  {...control}
                  maxLength={500}
                  autoComplete="off"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                />
              )}
            </Field>
          ) : null}
        </div>
        <TillAtClose point={point} till={till} />
        <ProblemNotice problem={problem} />
        <div>
          <Button
            type="submit"
            variant="staff"
            busy={busy}
            // Natively disabled while it waits (cash.spec): the reasons say for what.
            disabled={reasons.length > 0}
            aria-describedby={reasons.length > 0 ? reasonsId : undefined}
          >
            {m.cash.closeSubmit}
            <NamedFor name={point} />
          </Button>
        </div>
        <ReasonList id={reasonsId} reasons={reasons} />
      </SubPanel>
    </form>
  )
}

/**
 * The point's till closures: a Z typed on its own, and the latest ones. The Z
 * form opens in place of its button and takes the focus; Annulla gives it
 * back to the button, and a Z recorded says so where the form was.
 */
function Zs({ locationId, point, zs }: { locationId: string; point: string; zs: TillsRead }) {
  const { messages: m } = useLocale()
  const copy = m.fiscal.till
  const [alone, setAlone] = useState(false)
  const [recorded, setRecorded] = useState<number | null>(null)
  const box = useRef<HTMLDivElement>(null)
  const moved = useRef<'opened' | 'cancelled' | null>(null)

  useEffect(() => {
    if (moved.current === 'opened' && alone) box.current?.querySelector('input')?.focus()
    if (moved.current === 'cancelled' && !alone) box.current?.querySelector('button')?.focus()
    moved.current = null
  }, [alone])

  return (
    <div className="flex flex-col gap-3">
      {recorded === null ? null : (
        <OutcomeNotice key={recorded}>
          <p>{copy.recorded}</p>
        </OutcomeNotice>
      )}
      <div ref={box}>
        {alone ? (
          <TillForm
            locationId={locationId}
            point={point}
            onDone={() => {
              setAlone(false)
              setRecorded(Date.now())
            }}
            onCancel={() => {
              moved.current = 'cancelled'
              setAlone(false)
            }}
          />
        ) : (
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => {
              moved.current = 'opened'
              setRecorded(null)
              setAlone(true)
            }}
          >
            {copy.aloneTitle}
            <NamedFor name={point} />
          </Button>
        )}
      </div>
      <TillClosures point={point} read={zs} />
    </div>
  )
}

/** The latest closes, newest first, with both counts: the blind one is never hidden. */
function History({ sessions }: { sessions: readonly Session[] }) {
  const { locale, messages: m } = useLocale()
  const copy = m.cash.history
  const euro = (cents: number | null) => (cents === null ? '—' : formatEuro(cents, locale))
  return (
    <Section title={copy.title}>
      {sessions.length === 0 ? (
        <p className="text-text-muted">{copy.none}</p>
      ) : (
        <DataTable
          label={copy.title}
          rows={sessions}
          rowKey={(session) => session.id}
          columns={[
            { header: copy.point, cell: (session) => session.location.name, rowHeader: true },
            {
              header: copy.closedAt,
              cell: (session) =>
                session.closedAt
                  ? format(m.cash.openedLine, {
                      date: formatDateTime(locale, session.closedAt),
                      name: session.closedBy ?? '—',
                    })
                  : '—',
            },
            { header: copy.opening, cell: (session) => euro(session.openingCents), numeric: true },
            {
              header: copy.firstCount,
              cell: (session) => euro(session.firstCountCents),
              numeric: true,
            },
            {
              header: copy.finalCount,
              cell: (session) => euro(session.countedCents),
              numeric: true,
            },
            {
              header: copy.expected,
              cell: (session) => euro(session.expectedCents),
              numeric: true,
            },
            {
              header: copy.difference,
              cell: (session) => euro(session.differenceCents),
              numeric: true,
            },
            {
              header: copy.reason,
              cell: (session) => session.differenceReason ?? '',
              className: 'break-words',
            },
          ]}
        />
      )}
    </Section>
  )
}
